import React from "react"
import styled, { css } from "styled-components"
import { theme } from "../../../theme"
import { Chip, ChipProps } from "@mui/material"
import { TextRegular } from "../text"

type MuiProgressChipProps = "key" | "icon"

export type ProgressChipProps = Pick<ChipProps, MuiProgressChipProps> & {
  borderColor?: string
  beforeApprovalCheck?: boolean

  state: ProgressChipState
  approvalTop?: string
  approvalBottom?: string
  label: string
  maxWidth?: number
}

export enum ProgressChipState {
  done = "done",
  blocked = "blocked",
  active = "active",
  future = "future",
}

export const ProgressChip = (props: ProgressChipProps) => {
  const { borderColor, beforeApprovalCheck, state, approvalTop, approvalBottom, label, maxWidth, ...rest } = props

  return (
    <ProgressChipStyle
      {...rest}
      $borderColor={borderColor}
      $beforeApprovalCheck={beforeApprovalCheck}
      $state={state}
      $approval={!!approvalTop || !!approvalBottom}
      label={<TextRegular noWrap={!!maxWidth}>{label}</TextRegular>}
      $maxWidth={maxWidth}
    />
  )
}

const states = {
  done: css`
    color: ${theme.color.green7};
    background-color: ${theme.color.green9};

    & .MuiChip-icon {
      color: ${theme.color.green7};
    }
  `,
  blocked: css`
    background-color: ${theme.color.red2};
    color: ${theme.color.white};

    svg {
      color: ${theme.color.white};
    }
  `,
  active: css`
    color: ${theme.color.white};
    background-color: ${theme.color.blue2};

    svg {
      color: ${theme.color.white};
    }
  `,
  future: css`
    color: ${theme.color.grey2};
    background-color: ${theme.color.grey5};

    svg {
      color: ${theme.color.grey2};
    }
  `,
}

const ProgressChipStyle = styled(Chip)<{
  $borderColor?: string
  $beforeApprovalCheck?: boolean

  $state: ProgressChipState
  $approval?: boolean

  $maxWidth?: number
}>`
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-shrink: 1;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spacing.s};
  height: 42px;
  max-width: calc(${(p) => p.$maxWidth}% + 25px);
  padding: 4px 12px;
  border-radius: 34px;
  border: 5px solid ${theme.color.white};

  svg {
    min-width: ${theme.iconSize.m};
    min-height: ${theme.iconSize.m};
    width: ${theme.iconSize.l};
    height: ${theme.iconSize.l};
    margin: 0;
  }

  span {
    padding: 0;
  }

  &:not(:first-of-type) {
    margin-left: -25px;
  }

  &:not(:last-of-type) {
    padding-right: 25px;
  }

  ${(p) => states[p.$state]}

  ${(p) =>
    p.$approval &&
    css`
      padding-right: 12px;

      &:last-of-type {
        padding-right: 0;
      }
    `}

  ${(p) =>
    p.$beforeApprovalCheck &&
    css`
      padding-right: 0;
    `}
`
